import { query } from 'operations';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { GeneralIds, ModelTypes, QueryParams } from 'types/types';

interface RequestProps<T> {
  type: ModelTypes;
  ids?: GeneralIds;
  queryParams?: QueryParams;
  set?: {
    data: (data: T) => void;
    loading?: Dispatch<SetStateAction<boolean>>;
  };
  skip?: boolean;
  dependencies?: (string | undefined)[];
}

export default function useQuery<T>({
  type,
  ids,
  queryParams,
  set,
  skip,
  dependencies = [],
}: RequestProps<T>) {
  const [localLoading, setLocalLoading] = useState(true);
  const [localData, setLocalData] = useState<T>();
  const { data: setData, loading: setLoading } = set || {};

  async function fetchData(controller?: AbortController) {
    if (skip) return;

    setLoading?.(true);
    setLocalLoading(true);
    try {
      const { data } = await query<T>({
        type,
        ids,
        queryParams,
        signal: controller?.signal,
      });

      if (!controller?.signal.aborted) {
        setData?.(data);
        setLocalData(data);
      }
    } catch (error) {
      if (controller?.signal.aborted) {
        console.info('Fetch aborted');
      } else {
        console.error('Fetch error:', error);
      }
    } finally {
      if (!controller?.signal.aborted) {
        setLoading?.(false);
        setLocalLoading(false);
      }
    }
  }

  useEffect(() => {
    const controller = new AbortController();

    fetchData(controller);

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return { data: localData, loading: localLoading, refetch: fetchData };
}
